import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Button, Col, DatePicker, Input, message, Radio, Row, Select } from 'antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import 'dayjs/locale/ja';
import TextArea from 'antd/es/input/TextArea';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { HttpStatusCode } from 'axios';
import styles from './index.module.scss';
import {
  BusinessTypeKeys,
  EmergencyType,
  EmergencyTypeKeys,
  getEnumByKey,
  IssueBusinessTypes,
  IssueTypeOther,
  Priority,
  PriorityKeys,
  ProcessingStatusDisplay,
  ProcessingStatusKeys,
  SEARCH_EMPTY_SELECT,
} from '../../../constant';
import { IssueRequest } from '../../../model/issues_request';
import { DateFormat, formatDate } from '../../../utils/date_utils';
import { useDivisionSelector } from '../../components/division_selector/interface';
import { useSessionStore } from '../../../context/session';
import { ApiError } from '../../../api/api';
import { Messages } from '../../../constant/messages';
import { useIssueStore } from './issue_store';
import { useIndicator } from '../../../hook/use_indicator';
import { PermissionName } from '../../../valueobject/permissionName';

// 街区・フロア、区画プルダウン生成
const { Parts: { DivisionsProvider, Division, Floor, Block } } = useDivisionSelector();

/**
 * 臨時作業依頼画面
 * @constructor
 */
export const IssuePage: React.FC = () => {
  const navigator = useNavigate();
  const { loginInfo, selectedDivisionId, hasPermission } = useSessionStore((selector) => selector);

  const businessType = useMemo(() => {
    return (loginInfo.user.company.businessType === BusinessTypeKeys.all ? Object.values(IssueBusinessTypes)[0].key : getEnumByKey(BusinessTypeKeys, loginInfo.user.company.businessType));
  }, [loginInfo]);

  // 表示対象データID
  const { workId, planId } = useParams();
  // Form入力値の状態管理
  const [param, setParam] = useState<IssueRequest>();
  // 404時のエラー表示フラグ
  const [isError, setIsError] = useState(false);
  // 編集可能フラグ
  const [editable, setEditable] = useState(true);
  // 時刻用文字列
  const TIME_SUFFIX = ':00:00';
  // 発生事象プルダウン
  const [issueTypeOptions, setIssueTypeOptions] = useState<{ value: number; label: string }[]>([]);
  // 会社プルダウン
  const [companyOptions, setCompanyOptions] = useState<{ value: number; label: string }[]>([]);
  // 対象業務の選択値
  const [selectedBisunessType, setSelectedBisunessType] = useState(businessType);
  // 更新有無
  const [isUpdate, setIsUpdate] = useState(false);
  // Etag
  const [etag, setEtag] = useState('');
  // 作業計画ID
  const [workPlanId, setWorkPlanId] = useState(0);
  // インジケータ
  const indicator = useIndicator();
  // 担当会社プルダウン取得エラー
  const [isErrorCompanies, setIsErrorCompanies] = useState(false);
  // 発生事象プルダウン取得エラー
  const [isErrorIssues, setIsErrorIssues] = useState(false);
  // 完了メッセージ
  const [messageApi, contextHolder] = message.useMessage();

  const { getTaskDetails, getIncidents, getCompanies, registeIssue, updateIssue } = useIssueStore((selector) => selector);

  const location = useLocation();

  const [timeoutError, setTimeoutError] = useState(undefined);
  const [businessTypeOptions, setBusinessTypeOptions] = useState<{ value: BusinessTypeKeys; label: string }[]>([
    ...IssueBusinessTypes.map((type) => {
      return { value: type.key, label: type.value };
    }),
  ]);

  // 時間をプルダウン用に整形する
  const formatTimeForOption = (i: number) => {
    const h = (String(i).length === 1) ? '0' + String(i) : String(i);
    return h + TIME_SUFFIX;
  };

  useEffect(() => {
    if (timeoutError === undefined) {
      return;
    }
    window.alert(Messages.ERR_DIALOG_TIMEOUT);
  }, [timeoutError]);

  useEffect(() => {
    if (hasPermission(PermissionName.ALL_BUSINESSES, true)) {
      setBusinessTypeOptions([
        ...IssueBusinessTypes.map((type) => {
          return { value: type.key, label: type.value };
        }),
      ]);
      return;
    }
    const bType = loginInfo.user?.company?.businessType ?? BusinessTypeKeys.none;
    const getEnums = (key: BusinessTypeKeys) => {
      const res = IssueBusinessTypes.find((type) => type.key === key);
      if (res === undefined) {
        return { value: BusinessTypeKeys.none, label: '不明' };
      }
      return { value: res.key, label: res.value };
    };
    if (bType !== selectedBisunessType) {
      setBusinessTypeOptions([
        getEnums(selectedBisunessType),
      ]);
      return;
    }
    setBusinessTypeOptions([
      getEnums(bType),
    ]);
  }, [loginInfo, selectedBisunessType]);

  useEffect(() => {
    if (workId && planId) {
      indicator.show();
      setTimeoutError(undefined);
      // 既存データ取得
      getTaskDetails(Number(workId), Number(planId)).then((res) => {
        // データをセット
        const businessType = getEnumByKey(BusinessTypeKeys, res.businessType);
        setParam((param) => {
          return { ...param,
            businessType,
            companyId: res.companyId,
            issueTypeId: Number(res.details.issue_type_id),
            other: res.details.issue_type_other as unknown as string,
            taskSummary: res.details.task_summary as unknown as string,
            status: getEnumByKey(ProcessingStatusKeys, res.details.supported_status as unknown as string),
            issueDate: formatDate(dayjs(res.plan.start), DateFormat.YYYYMMDD_HYPHEN),
            issueTime: formatTimeForOption(dayjs(res.plan.start).hour()),
            divisionId: res.divisionId,
            floorId: res.floorId,
            blockId: res.blockId,
            target: res.target,
            emergencyType: getEnumByKey(EmergencyTypeKeys, res.details.urgency as unknown as string),
            periodDate: formatDate(dayjs(res.plan.end), DateFormat.YYYYMMDD_HYPHEN),
            periodTime: formatTimeForOption(dayjs(res.plan.end).hour()),
            priority: getEnumByKey(PriorityKeys, res.details.priority as unknown as string),
            processDetails: res.details.process_detailcep as unknown as string,
            note: res.details.memo as unknown as string,
            receptionist: res.details.receptionist as unknown as string,
            issuedFrom: res.details.issuedFrom as unknown as string,
            issuer: res.details.issuer as unknown as string,
            assigner: res.details.assigner as unknown as string,
          };
        });

        // 対応業務の初期値をセット
        setSelectedBisunessType(businessType);
        // 各フラグ初期化
        setEditable(res.editable); // 編集可能フラグ
        setIsUpdate(true); // 更新フラグ
        setEtag(res.etag); // etag
        setWorkPlanId(res.planId); // 作業計画ID

        // 画面表示時は画面上部を表示する
        window.scrollTo(0, 0);

      }).catch((e) => {
        if (e instanceof ApiError) {
          setIsError(true);
          const { status } = e.response;
          if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
            window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
            navigator('/login', { state: { message: Messages.ERR_401 } });
          } else if (status === -1) {
            setTimeoutError('timeout');
          } else if (status === HttpStatusCode.NotFound) {
            // アラート表示しない
          } else {
            window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
          }
        }
      }).finally(() => {
        indicator.hide();
      });

    } else {
      // 対応業務の初期値
      setSelectedBisunessType(businessType);

      // 初期値設定
      setParam((param) => {
        return {
          ...param,
          businessType, // 対応業務
          companyId: 0,
          issueTypeId: null,
          other: '',
          taskSummary: '',
          status: ProcessingStatusKeys.incomplete, // 対応状況
          issueDate: formatDate(dayjs(), DateFormat.YYYYMMDD_HYPHEN), // 発生日
          issueTime: formatTimeForOption(dayjs().hour()), // 発生時間
          divisionId: selectedDivisionId, // 街区
          floorId: 0,
          blockId: 0,
          target: '',
          emergencyType: null,
          periodDate: null,
          periodTime: null,
          priority: null,
          processDetails: '',
          note: '',
          receptionist: '',
          issuedFrom: '',
          issuer: '',
          assigner: '',
        };
      });

      // 各フラグ初期化
      setEditable(true); // 編集可能フラグ
      setIsUpdate(false); // 更新フラグ
      setEtag(''); // etag
      setWorkPlanId(0); // 作業計画ID

      // 画面表示時は画面上部を表示する
      window.scrollTo(0, 0);
    }
  }, [location.state]);

  useEffect(() => {
    indicator.show();
    setTimeoutError(undefined);
    // 発生事象一覧取得
    getIncidents(selectedBisunessType).then((res) => {
      const options = res.map((incident: { id: number; name: string }) => ({
        value: incident.id,
        label: incident.name,
      }));
      setIssueTypeOptions([...options, IssueTypeOther]);

      // 会社一覧取得
      getCompanies(selectedBisunessType).then((res) => {
        const options = res.map((incident: { id: number; name: string }) => ({
          value: incident.id,
          label: incident.name,
        }));
        setCompanyOptions([{ value: 0, label: SEARCH_EMPTY_SELECT }, ...options]);
      }).catch((e) => {
        if (e instanceof ApiError) {
          if (e.response.status === -1) {
            setTimeoutError('timeout');
          }
          setIsErrorCompanies(true);
        }
      });

    }).catch((e) => {
      if (e instanceof ApiError) {
        if (e.response.status === -1) {
          setTimeoutError('timeout');
        }
        setIsErrorIssues(true);
      }
    }).finally(() => {
      indicator.hide();
    });
  }, [selectedBisunessType]);

  /** 発生時間 */
  const timeOptions = [
    ...Array.from({ length: 24 }, (_, i) => {
      return { value: `${formatTimeForOption(i)}`, label: `${i}` };
    }),
  ];

  /** 登録処理 */
  const handleRegister = () => {
    indicator.show();
    registeIssue(param).then((res) => {
      // 完了メッセージ
      messageApi.success(Messages.EXECUTE_ISSUE_REGISTER);
      // 画面リロード
      navigator('/issue', { state: { refresh: true } });

    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  };

  /** 更新処理 */
  const handleUpdate = () => {
    indicator.show();
    updateIssue(param, workPlanId, etag).then((res) => {
      // 完了メッセージ
      messageApi.success(Messages.EXECUTE_ISSUE_UPDATE);
      // 画面リロード
      navigator(`/issue/${workId}/details/${planId}`, { state: { refresh: true } });

      indicator.hide();
    }).catch((e) => {
      indicator.hide();
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Conflict) {
          const res = window.confirm(Messages.ERR_DIALOG_CONFLICT);
          if (res) {
            indicator.show();
            updateIssue(param, workPlanId, etag, true).then((res) => {
              // 完了メッセージ
              messageApi.success(Messages.EXECUTE_ISSUE_UPDATE);
              // 画面リロード
              navigator(`/issue/${workId}/details/${planId}`, { state: { refresh: true } });

            }).catch((e) => {
              if (e instanceof ApiError) {
                const { status } = e.response;
                if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
                  window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
                  navigator('/login', { state: { message: Messages.ERR_401 } });
                } else if (status === -1) {
                  window.alert(Messages.ERR_DIALOG_TIMEOUT);
                } else {
                  window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
                }
              }
            }).finally(() => {
              indicator.hide();
            });
          }
        } else if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }
    });
  };

  /** 対応担当(業務)プルダウン変更時 */
  const handleBusinessType = (value:BusinessTypeKeys) => {
    setSelectedBisunessType(value);
    setParam((param) => {
      return { ...param, businessType: value, companyId: null };
    });
  };

  /** 発生事象プルダウン変更時 */
  const handleIssueType = (value:number) => {
    if (value === IssueTypeOther.value) {
      setParam((param) => {
        return { ...param, issueTypeId: value };
      });
    } else {
      setParam((param) => {
        return { ...param, issueTypeId: value, other: '' };
      });
    }
  };

  const button = useMemo(() => {
    const name = isUpdate ? '更新' : '登録';
    const onClick = isUpdate ? handleUpdate : handleRegister;
    const isEmpty = (val?: string | number | BusinessTypeKeys | null) => {
      return val === '' || val === null || val === undefined;
    };
    if (isEmpty(param?.issueTypeId) || isEmpty(param?.issueDate) || isEmpty(param?.businessType) || isEmpty(param?.issueTime) || isEmpty(param?.periodDate) || isEmpty(param?.periodTime) || (param?.issueTypeId === 0 && isEmpty(param?.other))) {
      return <Button disabled={true} type="primary" loading={indicator.showing} onClick={onClick}>{name}</Button>;
    }
    return <Button disabled={!editable} type="primary" loading={indicator.showing} onClick={onClick}>{name}</Button>;
  }, [isUpdate, editable, param, indicator]);


  if (isError) {
    if (timeoutError === 'timeout') {
      return (
          <div>
            <h1>臨時作業入力</h1>
            <div className={styles.section}>
              <Alert message={Messages.ERR_DIALOG_TIMEOUT} type="error" showIcon/>
            </div>
          </div>
      );
    }

    return (
        <div>
          <h1>臨時作業入力</h1>
          <div className={styles.section}>
            <Alert message={Messages.ERR_NO_DATA} type="error" showIcon/>
          </div>
        </div>
    );
  }

  return (
    <div>
      {contextHolder}
      <h1>臨時作業入力</h1>
      <div className={styles.section}>
        <form>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              対応担当(業務)
              <label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Select
                disabled={!editable}
                style={{ width: 120 }}
                value={selectedBisunessType}
                options={businessTypeOptions}
                onChange={handleBusinessType}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              担当会社
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              {
                (isErrorCompanies)
                  ? <Alert message={Messages.ERR_OTHER} type="error" showIcon/>
                  : <Select
                    disabled={!editable}
                    style={{ width: 120 }}
                    value={param?.companyId ?? 0}
                    options={companyOptions}
                    onChange={(value: number) => setParam((param) => {
                      return { ...param, companyId: value };
                    })}/>
              }
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              発生事象
              <label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              {
                (isErrorIssues)
                  ? <Alert message={Messages.ERR_OTHER} type="error" showIcon/>
                  : <Select
                    disabled={!editable}
                    style={{ width: 150 }}
                    value={param?.issueTypeId ?? null}
                    options={issueTypeOptions}
                    onChange={handleIssueType}/>
              }
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              その他
              <label style={{ visibility: (param?.issueTypeId === 0 ? 'visible' : 'hidden') }} className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Input
                className={styles.inputWidth}
                disabled={param?.issueTypeId !== 0 || !editable}
                value={param?.other ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, other: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              依頼内容
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <TextArea
                disabled={!editable}
                className={styles.inputWidth}
                rows={Math.min(15, Math.max(4, param?.taskSummary?.split('\n').length ?? 4))}
                value={param?.taskSummary ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, taskSummary: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              対応状況
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Radio.Group
                disabled={!editable}
                value={param?.status ?? ProcessingStatusKeys.incomplete}
                onChange={(e) => setParam((param) => {
                  return { ...param, status: e.target.value };
                })}
              >
                {ProcessingStatusDisplay.map((status) => (
                  <Radio key={status.key} value={status.key}>
                    {status.value}
                  </Radio>
                ))}
              </Radio.Group>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              発生日
              <label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <DatePicker
                disabled={!editable}
                locale={locale}
                value={param?.issueDate ? dayjs(param.issueDate) : dayjs()}
                allowClear={false}
                onChange={(value) => setParam((param) => {
                  return { ...param, issueDate: formatDate(value, DateFormat.YYYYMMDD_HYPHEN) };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              発生時間
              <label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Select
                disabled={!editable}
                style={{ width: 80, marginRight: '10px' }}
                value={param?.issueTime ?? formatTimeForOption(dayjs().hour())}
                options={timeOptions}
                onChange={(value) => setParam((param) => {
                  return { ...param, issueTime: value };
                })}
              />
              <span style={{ display: 'inline-block' }}>時頃</span>
            </Col>
          </Row>
          <Row className={styles.row2}>
            <DivisionsProvider defaultDivisionId={param?.divisionId ?? selectedDivisionId}
                               defaultFloorId={param?.floorId ?? 0} useLocalStorageIds={true}
                               defaultBlockId={param?.blockId ?? 0}
                               onChange={({ divisionId, floorId, blockId }) => {
                                 setParam({ ...param, divisionId, floorId, blockId });
                               }}
            >

              <div className={`${styles.formLabel} ${styles.item}`}>
                街区
              </div>
              <Col span={4} className={`${styles.item} ${styles.form_custom}`}>
                <Division disable={!editable} selectStyle={{ width: 120 }}/>
              </Col>
              <div className={styles.formLabelIssue}>
                階
              </div>
              <Col span={4} className={`${styles.item} ${styles.form_custom}`}>
                <Floor disable={!editable} selectStyle={{ width: 120 }}/>
              </Col>
              <div className={styles.formLabelIssue}>
                区画
              </div>
              <Col className={`${styles.item} ${styles.form_custom}`}>
                <Block disable={!editable} selectStyle={{ width: 120 }}/>
              </Col>
            </DivisionsProvider>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              対象箇所
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Input
                disabled={!editable}
                className={styles.inputWidth}
                value={param?.target ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, target: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              緊急度
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Radio.Group
                disabled={!editable}
                value={param?.emergencyType ?? null}
                onChange={(e) => setParam((param) => {
                  return { ...param, emergencyType: e.target.value };
                })}
              >
                {EmergencyType.map((type) => (
                  <Radio key={type.key} value={type.key}>
                    {type.value}
                  </Radio>
                ))}
              </Radio.Group>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              対応期限日
              <label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <DatePicker
                disabled={!editable}
                locale={locale}
                value={param?.periodDate ? dayjs(param.periodDate) : null}
                allowClear={false}
                onChange={(value) => setParam((param) => {
                  return { ...param, periodDate: formatDate(value, DateFormat.YYYYMMDD_HYPHEN) };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              対応期限時間
              <label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Select
                disabled={!editable}
                style={{ width: 80, marginRight: '10px' }}
                value={param?.periodTime ?? null}
                options={timeOptions}
                onChange={(value) => setParam((param) => {
                  return { ...param, periodTime: value };
                })}
              />
              <span style={{ display: 'inline-block' }}>時頃</span>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              優先度
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Radio.Group
                disabled={!editable}
                value={param?.priority ?? null}
                onChange={(e) => setParam((param) => {
                  return { ...param, priority: e.target.value };
                })}
              >
                {Priority.map((type) => (
                  <Radio key={type.key} value={type.key}>
                    {type.value}
                  </Radio>
                ))}
              </Radio.Group>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              処置内容
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <TextArea
                disabled={!editable}
                className={styles.inputWidth}
                rows={Math.min(10, Math.max(4, param?.processDetails?.split('\n').length ?? 4))}
                value={param?.processDetails ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, processDetails: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              備考
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <TextArea
                disabled={!editable}
                className={styles.inputWidth}
                rows={Math.min(10, Math.max(4, param?.note?.split('\n').length ?? 4))}
                value={param?.note ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, note: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              受付者
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Input
                disabled={!editable}
                className={styles.inputWidth}
                value={param?.receptionist ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, receptionist: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              依頼元
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Input
                disabled={!editable}
                className={styles.inputWidth}
                value={param?.issuedFrom ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, issuedFrom: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              依頼者
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Input
                disabled={!editable}
                className={styles.inputWidth}
                value={param?.issuer ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, issuer: e.target.value };
                })}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              担当者
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Input
                disabled={!editable}
                className={styles.inputWidth}
                value={param?.assigner ?? ''}
                onChange={(e) => setParam((param) => {
                  return { ...param, assigner: e.target.value };
                })}
              />
            </Col>
          </Row>
          <br/>
          <br/>
          {button}
        </form>
      </div>
    </div>
  );
};
